import { FC, useMemo } from 'react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { ReactComponent as SortArrow } from 'assets/icons/sort_arrow.svg';
import Pagination, { ITablePaginate } from 'components/Tables/Pagination';
import { capitalizeAvatarName } from 'utils/common';
import { MetricBreakdownTableRow } from 'hooks/useRoiAppointmentMetricBreakdown';
import { MetricColumn } from './METRIC_OPTIONS';

interface MetricBreakdownTableProps {
  columns: MetricColumn[];
  rows: MetricBreakdownTableRow[];
}

const PAGE_SIZE = 10;
const DATE_COLUMN_ID = 'apptDate';

const sortByNumericKey = (sortKey: string) => (rowA: any, rowB: any) =>
  Number(rowA.original[sortKey] ?? 0) - Number(rowB.original[sortKey] ?? 0);

const renderCell = (row: MetricBreakdownTableRow, column: MetricColumn) => {
  switch (column.renderer) {
    case 'datetime':
      return (
        <div className="flex flex-col">
          <span>{row.apptDate}</span>
          <span className="text-darkest-grey">{row.apptTime}</span>
        </div>
      );
    case 'avatar-name': {
      const fullName = String(row.patient ?? '');
      const [firstName, ...restName] = fullName.split(' ');
      const lastName = restName.join(' ');
      return (
        <div className="flex items-center gap-x-1.2">
          <div className="w-2.4 h-2.4 bg-magenta rounded-full flex items-center justify-center text-white text-[1rem] shrink-0">
            {capitalizeAvatarName(fullName)}
          </div>
          <div className="flex flex-col">
            <span>{firstName}</span>
            {lastName ? <span>{lastName}</span> : null}
          </div>
        </div>
      );
    }
    case 'practitioner':
      return (
        <div className="flex flex-col">
          <span>{row.practitioner}</span>
          {row.linkedAppointmentDoctorName ? (
            <span className="text-darkest-grey">
              {row.linkedAppointmentDoctorName}
            </span>
          ) : null}
        </div>
      );
    case 'amount': {
      const amount = Number(row[column.id] ?? 0);
      return <span>${amount.toLocaleString()}</span>;
    }
    case 'reason': {
      const reason = String(row.rejectedReason ?? '');
      return (
        <div className="flex items-center gap-x-0.8">
          <span>{reason}</span>
        </div>
      );
    }
    case 'text':
    default:
      return <span>{String(row[column.id] ?? '')}</span>;
  }
};

const MetricBreakdownTable: FC<MetricBreakdownTableProps> = ({
  columns,
  rows,
}) => {
  const tableColumns = useMemo(
    () =>
      columns.map((column) => ({
        Header: column.label,
        accessor: column.id,
        id: column.id,
        width: column.width,
        disableSortBy: !column.sortable,
        ...(column.sortKey
          ? { sortType: sortByNumericKey(column.sortKey) }
          : {}),
        Cell: ({ row }: any) =>
          renderCell(row.original as MetricBreakdownTableRow, column),
      })),
    [columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: tableColumns,
      data: rows,
      initialState: {
        pageSize: PAGE_SIZE,
        sortBy: [{ id: DATE_COLUMN_ID, desc: true }],
      },
      disableSortRemove: true,
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="flex flex-col" {...getTableProps()}>
      {headerGroups.map((headerGroup) => (
        <div
          {...headerGroup.getHeaderGroupProps()}
          className="flex border-b border-light-grey font-bold"
        >
          {headerGroup.headers.map((column) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              className={`${
                (column as any).width
              } flex items-center gap-x-0.9 py-1.9 cursor-pointer`}
            >
              <span className="uppercase text-dim-grey text-11 leading-[1.8rem]">
                {column.render('Header')}
              </span>
              {column.canSort && (
                <SortArrow
                  className={`${
                    column.isSorted ? 'fill-magenta' : 'fill-[#9CA3AF]'
                  } ${
                    column.isSorted && !column.isSortedDesc ? 'rotate-180' : ''
                  } transition-all`}
                />
              )}
            </div>
          ))}
        </div>
      ))}

      {rows.length === 0 ? (
        <div className="py-3 text-center text-14 text-mid-grey">No data</div>
      ) : (
        <div {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <div
                {...row.getRowProps()}
                className="flex items-center border-b border-light-grey py-1.6 text-14 leading-[2.1rem] text-darkest-grey"
              >
                {row.cells.map((cell) => (
                  <div
                    {...cell.getCellProps()}
                    className={(cell.column as any).width}
                  >
                    {cell.render('Cell')}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {rows.length > 0 && (
        <div className="mt-1.6">
          <Pagination
            isBorderNotIncluded
            data={{
              currPage: pageIndex + 1,
              perPage: pageSize,
              total: rows.length,
            }}
            handlePaginating={(paginate: ITablePaginate) => {
              gotoPage(paginate.currPage - 1);
              setPageSize(paginate.perPage);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MetricBreakdownTable;
